import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function SubmitReimbursementDialog({ opa, onClose, onSubmitted }) {
  const [formNumber, setFormNumber] = useState("");
  const [totalValue, setTotalValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (opa) {
      setFormNumber("");
      setTotalValue("");
      setConfirming(false);
    }
  }, [opa]);

  if (!opa) return null;

  const handleClick = () => {
    if (!formNumber.trim()) return toast.error("Form Number is required");
    const v = parseFloat(totalValue);
    if (isNaN(v) || v < 0) return toast.error("Total Value must be a non-negative number");
    if (!confirming) { setConfirming(true); return; }
    submit(v);
  };

  const submit = async (v) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/opa-requests/${opa.id}/reimbursement`, {
        form_number: formNumber.trim(),
        total_value: v,
      });
      toast.success(`Reimbursement submitted for ${opa.code} · moved to Reimbursement Submitted`);
      onSubmitted && onSubmitted(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Submission failed");
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!opa} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-zinc-100 sm:max-w-md"
        data-testid="reimbursement-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <FileSpreadsheet className="w-5 h-5 text-violet-400" /> Submit Reimbursement Form
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Recording reimbursement claim for{" "}
            <span className="font-mono text-emerald-400">{opa.code}</span> (released{" "}
            <span className="font-mono text-white">
              {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(opa.amount || 0)}
            </span>
            )
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs text-zinc-400">Reimbursement Form Number <span className="text-red-400">*</span></Label>
            <Input
              value={formNumber}
              onChange={(e) => setFormNumber(e.target.value)}
              placeholder="e.g., RMB-2026-0001"
              className="mt-1.5 bg-zinc-900 border-white/10 text-white"
              data-testid="reimbursement-form-number"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Total Value (₹) <span className="text-red-400">*</span></Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={totalValue}
              onChange={(e) => setTotalValue(e.target.value)}
              placeholder="e.g., 1700"
              className="mt-1.5 bg-zinc-900 border-white/10 text-white"
              data-testid="reimbursement-total-value"
            />
            <p className="text-[11px] text-zinc-500 mt-1">Final accounted spend against this advance.</p>
          </div>
        </div>
        <DialogFooter className="mt-4 flex-col gap-3 sm:flex-col sm:items-stretch">
          {confirming && (
            <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2" data-testid="reimbursement-confirm-banner">
              Are you sure? Click <span className="font-medium">Confirm Submit</span> again to record this reimbursement and move the OPA to <span className="font-medium">Reimbursement Submitted</span>.
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => { if (confirming) setConfirming(false); else onClose(); }} disabled={saving} className="text-zinc-400 hover:text-white" data-testid="reimbursement-cancel">{confirming ? "Go back" : "Cancel"}</Button>
            <Button
              onClick={handleClick}
              disabled={saving}
              className={confirming ? "bg-amber-500 text-black hover:bg-amber-400 font-medium" : "bg-violet-500 text-black hover:bg-violet-400 font-medium"}
              data-testid="reimbursement-submit"
            >
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {confirming ? "Confirm Submit" : "Submit Form"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
